import { utils as xlsxUtils, writeFile as xlsxWriteFile } from "xlsx";

export interface ImportStats {
  goal_banks_created?: number;
  goal_banks_skipped?: number;
  goal_banks_updated?: number;
  goal_folders_created?: number;
  rows_skipped_empty?: number;
  target_banks_created?: number;
  target_banks_skipped?: number;
  target_banks_updated?: number;
  errors?: { row?: number; row_index?: number; sheet?: string; error: string }[];
}

export const parseStats = (raw: unknown): ImportStats => {
  if (!raw) return {};
  try {
    return typeof raw === "string" ? (JSON.parse(raw) as ImportStats) : (raw as ImportStats);
  } catch {
    return {};
  }
};

// `rows_skipped_empty` is one counter shared by both sheets on the backend.
// Deriving the Goals-only count from `errors` (which tags Targets-sheet
// entries with `sheet: "Targets"`, leaving Goals-sheet entries untagged)
// avoids mislabeling Targets-sheet skips as a Goals-sheet problem.
export const countGoalRowsSkipped = (stats: ImportStats): number =>
  stats.errors?.length ? stats.errors.filter((e) => e.sheet !== "Targets").length : stats.rows_skipped_empty ?? 0;

export const resolveSkipRowLabel = (error: { row?: number; row_index?: number; sheet?: string }): string => {
  const rowNum = error.row ?? (error.row_index == null ? undefined : error.row_index + 2);
  const prefix = error.sheet ? `${error.sheet} sheet, ` : "";
  return rowNum == null ? "" : `${prefix}Row ${rowNum}: `;
};

// A single source of truth for "would this import actually write anything?" --
// used to both disable the confirm button and to show the empty-state message,
// so the two can't drift out of sync.
export const hasImportableChanges = (stats: ImportStats): boolean =>
  !!(stats.goal_banks_created || stats.goal_banks_updated || stats.target_banks_created || stats.target_banks_updated);

// The confirm button's label should reflect what's actually about to be
// written -- a target-only file (no new/updated goals) importing under the
// literal label "Import goals" would be confusing.
export const resolveImportButtonLabel = (stats: ImportStats): string => {
  const hasGoals = !!(stats.goal_banks_created || stats.goal_banks_updated);
  const hasTargets = !!(stats.target_banks_created || stats.target_banks_updated);
  if (hasGoals && hasTargets) return "Import goals & targets";
  if (hasTargets) return "Import targets";
  return "Import goals";
};

// Builds a blank two-sheet workbook with the exact headers the backend parser
// expects (see FOLDER_NAME_COL/GOAL_NAME_COL/etc. in frontera's
// api/mutations/admin/goal_bank.py) and triggers a browser download, so users
// don't have to guess column names/casing by hand.
export const downloadGoalBankImportTemplate = (): void => {
  const workbook = xlsxUtils.book_new();
  const goalsSheet = xlsxUtils.aoa_to_sheet([["Folder Name", "Goal Name", "Mastery Criteria"]]);
  const targetsSheet = xlsxUtils.aoa_to_sheet([["Folder Name", "Goal Name", "Target Name", "Mastery Criteria"]]);
  xlsxUtils.book_append_sheet(workbook, goalsSheet, "Goals");
  xlsxUtils.book_append_sheet(workbook, targetsSheet, "Targets");
  xlsxWriteFile(workbook, "goal-bank-import-template.xlsx");
};
